import { FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useUpsertAlertSetting } from '@hooks/api/alerts/alert_setting';
import { useSession } from '@hooks/useSession';

import type { AlertParameterModel } from 'generated/types';

const messages = defineMessages({
  title: {
    id: 'alertSettingTable.notifications.title',
    defaultMessage:
      'Alert Notifications: {enabled, select, true{Enabled} other{Disabled}}',
  },
  caption: {
    id: 'alertSettingTable.notifications.caption',
    defaultMessage: 'Emails and texts will be sent to recipients',
  },
});

interface NotificationToggleProps {
  enabled: boolean;
  alert_setting_id: string;
  parameters: AlertParameterModel[];
}

export default function NotificationToggle({
  alert_setting_id,
  enabled,
  parameters,
}: NotificationToggleProps) {
  const { formatMessage } = useIntl();
  const {
    state: { activeProfile, isPractitioner },
  } = useSession();
  const [vitall_patient_id, account_id = ''] = [
    activeProfile?.vitall_patient_id ?? '',
    activeProfile?.account_id,
  ];

  const [updateAlertSetting] = useUpsertAlertSetting();

  const updateSetting = async (alert_setting_id: string, enabled: boolean) => {
    await updateAlertSetting({
      alert_setting_id,
      vitall_patient_id,
      account_id,
      changes: {
        parameters: parameters.map(({ alert_parameter_id }) => ({
          reference_range_condition_id: alert_parameter_id,
          notification_enabled: enabled,
        })),
      },
    });
  };

  return (
    <FormControlLabel
      control={
        <Switch
          checked={enabled}
          disabled={!isPractitioner}
          onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
            e.preventDefault();
            e.stopPropagation();
            await updateSetting(alert_setting_id, e.target.checked);
          }}
          onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.stopPropagation();
          }}
        />
      }
      label={
        <Stack spacing={0}>
          <Typography variant="body1">
            {formatMessage(messages.title, {
              enabled,
            })}
          </Typography>
          <Typography variant="caption">
            {formatMessage(messages.caption)}
          </Typography>
        </Stack>
      }
      labelPlacement="start"
      sx={{ ml: 0 }}
    />
  );
}
